<template>
  <b-modal
    v-model="show"
    title="Preview Soal"
    centered
    hide-footer
    size="xl"
    @hidden="clearBodyScrollbarPadding"
  >
    <div v-if="data">
      <!-- PERTANYAAN -->
      <div class="mb-3">
        <h6>No. {{ data.nomor_urut }}</h6>

        <!-- STUDI KASUS -->
        <div v-if="data.studi_kasus" class="mb-3 p-3 bg-light rounded border">
          <div class="fw-bold mb-1 text-primary">Studi Kasus:</div>
          <div style="white-space: pre-line">{{ data.studi_kasus }}</div>
        </div>

        <p>{{ data.pertanyaan }}</p>

        <div class="text-center my-3">
          <img
            v-if="data.pertanyaan_gambar"
            :src="getImageUrl(data.pertanyaan_gambar)"
            class="soal-image"
          />
        </div>
      </div>

      <!-- OBJECTIVE -->
      <div v-if="isObjectiveType">
        <h6>Jawaban:</h6>
        <div
          v-for="jawab in data.jawaban"
          :key="jawab.idverif_jawaban"
          class="mb-2 p-2 border rounded"
          :class="{ 'border-success bg-light': jawab.skor === 1 }"
        >
          <div class="d-flex align-items-center">
            <div style="width: 40px" v-if="tipeNormalized == 'pilihan ganda'">
              <b>{{ jawab.kode_jawaban }}</b>
            </div>
            <div class="flex-grow-1">
              {{ jawab.text_jawaban }}
            </div>
            <span v-if="jawab.skor === 1" class="badge bg-success">
              Benar
            </span>
          </div>
        </div>
      </div>

      <!-- MATCHING -->
      <div v-else-if="isMatchingType">
        <h6>Pasangan Jawaban:</h6>
        <div
          v-for="jawab in data.jawaban"
          :key="jawab.idverif_jawaban"
          class="mb-2 p-2 border rounded"
          :class="{ 'border-success bg-light': jawab.skor === 1 }"
        >
          <div class="d-flex align-items-center">
            <div class="flex-grow-1">
              {{ jawab.text_jawaban }}
            </div>
            <span v-if="jawab.skor === 1" class="badge bg-success">
              Benar
            </span>
          </div>
        </div>
      </div>

      <!-- ESSAY / LEVEL 4 KEYWORDS -->
      <div v-else-if="isEssayType">
        <h6>Keyword Jawaban:</h6>

        <div v-if="keywordList && keywordList.length" class="d-flex flex-wrap gap-2">
          <span
            v-for="(item, idx) in keywordList"
            :key="idx"
            class="badge bg-primary px-3 py-2 font-size-13 rounded-pill"
            style="text-wrap: wrap; text-align: left;"
          >
            {{ item }}
          </span>
        </div>

        <div v-else>
          <p class="text-muted">Belum ada keyword jawaban</p>
        </div>
      </div>

      <!-- FALLBACK -->
      <div v-else>
        <p class="text-muted">Tipe soal belum didukung</p>
      </div>

      <hr />

      <div class="meta-container">
        <h6 class="mb-3">Informasi Soal</h6>

        <div class="row">
          <!-- Konsep Teori -->
          <div class="col-md-6 mb-3">
            <div class="meta-card">
              <div class="meta-label">Konsep Teori</div>
              <div class="meta-value">
                {{ data.konsep_teori || "-" }}
              </div>
            </div>
          </div>

          <!-- Referensi -->
          <div class="col-md-6 mb-3">
            <div class="meta-card">
              <div class="meta-label">Referensi</div>
              <div class="meta-value">
                {{ data.referensi || "-" }}
              </div>
            </div>
          </div>

          <!-- Standar Kompetensi -->
          <div class="col-md-6 mb-3">
            <div class="meta-card">
              <div class="meta-label">Standar Kompetensi</div>
              <div class="meta-value">
                {{ data.standar_kompetensi || "-" }}
              </div>
            </div>
          </div>

          <!-- Status -->
          <div class="col-md-6 mb-3">
            <div
              class="meta-card d-flex justify-content-between align-items-center"
            >
              <div>
                <div class="meta-label">Status</div>
                <div class="meta-value">
                  {{ data.status_soal }}
                </div>
              </div>

              <!-- Badge status -->
              <span class="badge px-3 py-2" :class="statusClass">
                {{ data.status_soal }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- CATATAN -->
      <div class="mt-3" v-if="catatanList.length">
        <h6 class="mb-2">Catatan Verifikasi</h6>
        <ul class="list-group catatan-verifikasi-list">
          <li
            v-for="catatan in catatanList"
            :key="catatan.id"
            class="list-group-item"
          >
            <div
              class="d-flex justify-content-between align-items-start gap-2 mb-1"
            >
              <strong class="small">
                {{ catatan.validator || "Validator" }}
              </strong>
              <span class="badge bg-warning text-dark">Catatan</span>
            </div>
            <div class="small text-body">
              {{ catatan.text }}
            </div>
          </li>
        </ul>
      </div>
    </div>
  </b-modal>
</template>

<style scoped>
.soal-image {
  max-width: 100%;
  max-height: 400px; /* biar gak kepanjangan */
  object-fit: contain; /* biar proporsional */
  border-radius: 8px;
  border: 1px solid #ddd;
  padding: 4px;
  background: #fff;
}
.meta-container {
  background: #f8f9fa;
  border-radius: 10px;
  padding: 16px;
}

.meta-card {
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 12px 14px;
  height: 100%;
}

.meta-label {
  font-size: 12px;
  color: #6c757d;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.meta-value {
  font-size: 14px;
  font-weight: 500;
  color: #212529;
  word-break: break-word;
}

.catatan-verifikasi-list .list-group-item {
  border-color: #f1d88a;
}
</style>

<script>
// import { data } from 'jquery';

export default {
  props: {
    modelValue: Boolean,
    data: Object,
    link: String,
  },
  computed: {
    show: {
      get() {
        return this.modelValue;
      },
      set(val) {
        this.$emit("update:modelValue", val);
      },
    },
    tipeNormalized() {
      return this.data?.tipe?.toLowerCase() || "";
    },
    isObjectiveType() {
      return ["pilihan ganda", "benar salah"].includes(this.tipeNormalized);
    },
    isMatchingType() {
      return this.tipeNormalized === "menjodohkan";
    },
    isEssayType() {
      return (
        ["text", "txt", "essay", "essai", "uraian", "uraian / teks"].includes(
          this.tipeNormalized,
        ) ||
        Boolean(this.data?.keyword_soal?.length) ||
        Boolean(this.data?.keywordSoal?.length)
      );
    },
    keywordList() {
      if (this.data?.keyword_soal?.length) {
        return this.data.keyword_soal
          .map((k) => (typeof k === "string" ? k : k.keyword || k.text || k.text_jawaban))
          .filter(Boolean);
      }
      if (this.data?.keywordSoal?.length) {
        return this.data.keywordSoal
          .map((k) => (typeof k === "string" ? k : k.keyword || k.text || k.text_jawaban))
          .filter(Boolean);
      }
      if (this.data?.jawaban?.length) {
        return this.data.jawaban
          .map((j) => (typeof j === "string" ? j : j.text_jawaban || j.keyword))
          .filter(Boolean);
      }
      return [];
    },
    statusClass() {
      const status = this.data?.status_soal;

      if (["DRAFT", "KOREKSI"].includes(status)) return "bg-warning";
      if (status === "PENDING") return "bg-info";
      if (status === "REVISI") return "bg-danger";
      if (status === "APPROVED") return "bg-success";

      return "bg-secondary";
    },
    catatanList() {
      return this.normalizeCatatanList(this.data?.catatan);
    },
  },
  methods: {
    clearBodyScrollbarPadding() {
      document.body.style.removeProperty("padding-right");
      document.body.removeAttribute("data-bs-padding-right");
    },
    getImageUrl(path) {
      return this.link + path;
    },
    normalizeCatatanList(catatan) {
      if (!catatan) return [];

      if (typeof catatan === "string") {
        const trimmedCatatan = catatan.trim();
        if (!trimmedCatatan) return [];

        try {
          const parsedCatatan = JSON.parse(trimmedCatatan);
          return this.normalizeCatatanList(parsedCatatan);
        } catch {
          return [{ id: 0, validator: null, text: trimmedCatatan }];
        }
      }

      if (Array.isArray(catatan)) {
        return catatan
          .map((item, index) => this.normalizeCatatanItem(item, index))
          .filter((item) => item.text);
      }

      if (typeof catatan === "object") {
        const normalizedItem = this.normalizeCatatanItem(catatan, 0);
        return normalizedItem.text ? [normalizedItem] : [];
      }

      return [];
    },
    normalizeCatatanItem(item, index) {
      if (typeof item === "string") {
        return {
          id: index,
          validator: null,
          text: item.trim(),
        };
      }

      if (!item || typeof item !== "object") {
        return { id: index, validator: null, text: "" };
      }

      return {
        id: item.id || item.idverif_soal_validator || index,
        validator:
          item.nama_validator ||
          item.validator_name ||
          item.validator?.nama ||
          item.validator?.name ||
          item.user?.nama ||
          item.user?.name ||
          null,
        text: String(
          item.catatan ||
          item.catatan_validator ||
          item.note ||
          item.feedback ||
          item.keterangan ||
            "",
        ).trim(),
      };
    },
  },
};
</script>
